'use client';

import { useState, useEffect, useMemo } from 'react';
import { X, TrendingUp, TrendingDown, Clock, Target, ShieldAlert, DollarSign, ArrowUpRight, ArrowDownRight, ExternalLink, UserPlus, Check, Send } from 'lucide-react';
import {  } from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { TradeCardData, TradeDirection, Comment } from './TradeCard';
import { marketColors } from './trade-types';
import SignalChartPlayer from './SignalChartPlayer';
import IdeaTypeBadge from '@/components/common/IdeaTypeBadge';
import { parseServerDate } from '@/lib/dates';
import { uploadCommentImage } from '@/lib/commentImage';
import { CommentImageButton, CommentImageDraft, CommentImageView, makePasteHandler } from './CommentImageParts';
import { useLang } from '@/lib/i18n/LanguageContext';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatPrice(price: number): string {
  // Analyses carry no levels — guard so a null/NaN never renders as a nonsensical number.
  if (price == null || !Number.isFinite(price)) return '—';
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 });
}

function formatPriceShort(price: number): string {
  if (price >= 10000) return price.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (price >= 100) return price.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 });
}

function formatTime(dateStr: string, t: (text: string) => string): string {
  const d = parseServerDate(dateStr);
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (diff < 60) return t('{time} ago').replace('{time}', `${diff}s`);
  if (diff < 3600) return t('{time} ago').replace('{time}', `${Math.floor(diff / 60)}m`);
  if (diff < 86400) return t('{time} ago').replace('{time}', `${Math.floor(diff / 3600)}h`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function calcRR(entry: number, sl: number, tp: number, direction: TradeDirection): number | null {
  const risk = direction === 'BUY' ? entry - sl : sl - entry;
  const reward = direction === 'BUY' ? tp - entry : entry - tp;
  if (risk <= 0 || reward <= 0) return null;
  return reward / risk;
}

function calcPoints(a: number, b: number): string {
  const diff = Math.abs(a - b);
  if (diff < 0.01) return diff.toFixed(5);
  if (diff < 1) return diff.toFixed(4);
  if (diff < 100) return diff.toFixed(2);
  return diff.toFixed(0);
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const { supabase } = await import('@/lib/supabase/client');
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) return { Authorization: `Bearer ${session.access_token}` };
  } catch {}
  return {};
}

const timeframeLabels: Record<string, string> = {
  M1: '1m', M5: '5m', M15: '15m', M30: '30m', H1: '1H', H4: '4H', D1: 'D1',
};

// ─── R:R Breakdown ───────────────────────────────────────────────────────────

function RRBreakdown({ trade }: { trade: TradeCardData }) {
  const { t } = useLang();
  const { entryPrice, stopLoss, takeProfit1, takeProfit2, takeProfit3, direction } = trade;
  const isBuy = direction === 'BUY';
  const risk = isBuy ? entryPrice - stopLoss : stopLoss - entryPrice;
  const riskPct = entryPrice > 0 ? ((risk / entryPrice) * 100).toFixed(2) : '0.00';
  const tps = [
    { label: 'TP1', price: takeProfit1 },
    ...(takeProfit2 ? [{ label: 'TP2', price: takeProfit2 }] : []),
    ...(takeProfit3 ? [{ label: 'TP3', price: takeProfit3 }] : []),
  ];
  const bestTP = tps[tps.length - 1];
  const bestRR = calcRR(entryPrice, stopLoss, bestTP.price, direction);

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)', background: 'var(--bg-sunken)' }}>
      <div className="px-4 py-2.5 border-b" style={{ borderColor: 'var(--border)' }}>
        <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>{t('R:R Breakdown')}</span>
      </div>
      <div className="p-4 flex flex-col gap-3">
        {/* Risk */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: 'var(--bearish)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{t('Risk')}</span>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Entry {isBuy ? '−' : '+'} SL</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-semibold" style={{ color: 'var(--bearish)' }}>{calcPoints(entryPrice, stopLoss)} pts</span>
            <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'var(--bearish-muted)', color: 'var(--bearish)' }}>{riskPct}%</span>
          </div>
        </div>
        {/* TPs */}
        {tps.map(({ label, price }) => {
          const rr = calcRR(entryPrice, stopLoss, price, direction);
          const reward = isBuy ? price - entryPrice : entryPrice - price;
          const rewardPct = entryPrice > 0 ? ((reward / entryPrice) * 100).toFixed(2) : '0.00';
          const tpColor = label === 'TP1' ? 'var(--bullish)' : label === 'TP2' ? 'var(--bullish)' : 'var(--bullish)';
          return (
            <div key={label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ background: tpColor }} />
                <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{t('Reward')} {label}</span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{isBuy ? 'TP − Entry' : 'Entry − TP'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-semibold" style={{ color: 'var(--bullish)' }}>{calcPoints(price, entryPrice)} pts</span>
                <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'var(--bullish-muted)', color: 'var(--bullish)' }}>{rewardPct}%</span>
                {rr !== null && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded" style={{
                    background: rr >= 2 ? 'var(--bullish-muted)' : rr >= 1 ? 'var(--win-muted)' : 'var(--bearish-muted)',
                    color: rr >= 2 ? 'var(--bullish)' : rr >= 1 ? 'var(--win)' : 'var(--bearish)',
                  }}>1:{rr.toFixed(1)}</span>
                )}
              </div>
            </div>
          );
        })}
        {/* Best R:R */}
        {bestRR !== null && (
          <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t('Best R:R')} ({bestTP.label})</span>
            <span className="font-mono font-bold text-base px-3 py-1 rounded-lg" style={{
              background: bestRR >= 2 ? 'var(--bullish-muted)' : bestRR >= 1 ? 'var(--win-muted)' : 'var(--bearish-muted)',
              color: bestRR >= 2 ? 'var(--bullish)' : bestRR >= 1 ? 'var(--win)' : 'var(--bearish)',
            }}>1:{bestRR.toFixed(2)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Signal Pills ────────────────────────────────────────────────────────────

function SignalPill({ label, sublabel, price, color, bg, icon, rr }: {
  label: string; sublabel: string; price: number; color: string; bg: string; icon: React.ReactNode; rr?: string | null;
}) {
  return (
    <div className="flex flex-col items-center gap-1 px-4 py-3 rounded-xl shrink-0" style={{ background: bg, border: '1px solid transparent', minWidth: 100 }}>
      <div className="flex items-center gap-1.5">
        <span style={{ color }}>{icon}</span>
        <span className="text-xs font-semibold" style={{ color }}>{label}</span>
      </div>
      <span className="font-mono text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{formatPrice(price)}</span>
      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{sublabel}</span>
      {rr && <span className="text-xs font-semibold" style={{ color }}>1:{rr}</span>}
    </div>
  );
}

function SignalSection({ trade, outcomePrice }: { trade: TradeCardData; outcomePrice?: number }) {
  const { t } = useLang();
  const { entryPrice, stopLoss, takeProfit1, takeProfit2, takeProfit3, direction, status } = trade;
  const isBuy = direction === 'BUY';
  const risk = isBuy ? entryPrice - stopLoss : stopLoss - entryPrice;
  const rr1 = risk > 0 ? ((isBuy ? takeProfit1 - entryPrice : entryPrice - takeProfit1) / risk).toFixed(1) : null;
  const rr2 = takeProfit2 && risk > 0 ? ((isBuy ? takeProfit2 - entryPrice : entryPrice - takeProfit2) / risk).toFixed(1) : null;
  const rr3 = takeProfit3 && risk > 0 ? ((isBuy ? takeProfit3 - entryPrice : entryPrice - takeProfit3) / risk).toFixed(1) : null;
  const exitColor = status === 'WIN' ? 'var(--bullish)' : status === 'LOSS' ? 'var(--bearish)' : 'var(--win)';
  const exitBg = status === 'WIN' ? 'var(--bullish-muted)' : status === 'LOSS' ? 'var(--bearish-muted)' : 'var(--win-muted)';

  return (
    <div>
      <p className="text-xs font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>{t('Entry / Exit Signals')}</p>
      <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
        <SignalPill label={isBuy ? '▲ BUY' : '▼ SELL'} sublabel={t('Entry Zone')} price={entryPrice}
          color={isBuy ? 'var(--bullish)' : 'var(--bearish)'} bg={isBuy ? 'var(--bullish-muted)' : 'var(--bearish-muted)'}
          icon={isBuy ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />} />
        <SignalPill label="✕ STOP" sublabel={t('Invalidation')} price={stopLoss}
          color="var(--bearish)" bg="var(--bearish-muted)" icon={<ShieldAlert size={12} />} />
        <SignalPill label="◎ TP1" sublabel={t('Target 1')} price={takeProfit1}
          color="var(--bullish)" bg="var(--bullish-muted)" icon={<Target size={12} />} rr={rr1} />
        {takeProfit2 && <SignalPill label="◎ TP2" sublabel={t('Target 2')} price={takeProfit2}
          color="var(--bullish)" bg="var(--bullish-muted)" icon={<Target size={12} />} rr={rr2} />}
        {takeProfit3 && <SignalPill label="◎ TP3" sublabel={t('Target 3')} price={takeProfit3}
          color="var(--bullish)" bg="var(--bullish-muted)" icon={<Target size={12} />} rr={rr3} />}
        {outcomePrice && (status === 'WIN' || status === 'LOSS' || status === 'BE') && (
          <SignalPill
            label={status === 'WIN' ? '✓ EXIT WIN' : status === 'LOSS' ? '✗ EXIT LOSS' : '~ EXIT BE'}
            sublabel={t('Closed at')} price={outcomePrice} color={exitColor} bg={exitBg}
            icon={<DollarSign size={12} />} />
        )}
      </div>
    </div>
  );
}

// ─── Comments Section ────────────────────────────────────────────────────────

function CommentsSection({ tradeId, initialComments }: { tradeId: string; initialComments: Comment[] }) {
  const { t } = useLang();
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const imagePreview = useMemo(() => (image ? URL.createObjectURL(image) : null), [image]);
  useEffect(() => {
    return () => { if (imagePreview) URL.revokeObjectURL(imagePreview); };
  }, [imagePreview]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/trades/${tradeId}/comments`);
        if (res.ok) {
          const data = await res.json();
          setComments(data.comments || []);
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [tradeId]);

  async function handlePost(e: React.FormEvent) {
    e.preventDefault();
    if ((!text.trim() && !image) || posting) return;
    const content = text.trim();
    setPosting(true);
    try {
      // Upload the attached chart first (compressed client-side, moderated server-side).
      let imageUrl: string | undefined;
      if (image) imageUrl = await uploadCommentImage(image);

      const authHeaders = await getAuthHeaders();
      const res = await fetch(`/api/trades/${tradeId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ content, imageUrl }),
      });
      if (res.ok) {
        const data = await res.json();
        setComments((prev) => [data.comment, ...prev]);
        setText('');
        setImage(null);
        toast.success(t('Comment posted'));
      } else {
        const err = await res.json();
        toast.error(err.error || t('Failed to post comment'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('Network error'));
    }
    setPosting(false);
  }

  return (
    <div>
      <p className="text-xs font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>
        {t('Comments')} {comments.length > 0 && <span style={{ color: 'var(--text-muted)' }}>({comments.length})</span>}
      </p>
      {user && (
        <form onSubmit={handlePost} className="flex gap-2 mb-4">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
            style={{ background: 'var(--accent)', color: 'var(--bg-base)' }}>
            {(user.user_metadata?.username || user.email || 'U')[0].toUpperCase()}
          </div>
          <div className="flex-1">
            {imagePreview && (
              <CommentImageDraft preview={imagePreview} uploading={posting} onRemove={() => setImage(null)} />
            )}
            <div className="flex gap-2">
              <input type="text" value={text} onChange={(e) => setText(e.target.value)}
                onPaste={makePasteHandler(setImage)}
                placeholder={t('Add your analysis…')} className="input-base flex-1 px-3 py-2 text-sm" />
              <CommentImageButton disabled={posting} onPick={setImage} />
              <button type="submit" disabled={(!text.trim() && !image) || posting}
                className="btn-primary px-3 py-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1">
                <Send size={12} />{posting ? t('Posting…') : t('Post')}
              </button>
            </div>
          </div>
        </form>
      )}
      {loading ? (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-2 animate-pulse">
              <div className="w-7 h-7 rounded-full shrink-0" style={{ background: 'var(--bg-sunken)' }} />
              <div className="flex-1 space-y-2">
                <div className="h-3 rounded w-24" style={{ background: 'var(--bg-sunken)' }} />
                <div className="h-3 rounded w-full" style={{ background: 'var(--bg-sunken)' }} />
              </div>
            </div>
          ))}
        </div>
      ) : comments.length === 0 ? (
        <p className="text-xs text-center py-6" style={{ color: 'var(--text-muted)' }}>{t('No comments yet. Be the first to share your analysis.')}</p>
      ) : (
        <div className="flex flex-col gap-4">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>
                {c.authorInitial}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>@{c.author}</span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{formatTime(c.createdAt, t)}</span>
                </div>
                {c.content && (
                  <p className="text-sm mt-0.5 leading-relaxed" style={{ color: 'var(--text-body)' }}>{c.content}</p>
                )}
                {c.imageUrl && <CommentImageView url={c.imageUrl} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Modal inner content (shared between mobile/desktop) ─────────────────────

interface ModalContentProps {
  trade: TradeCardData;
  isBuy: boolean;
  isOwner: boolean;
  marketColor: { bg: string; text: string };
  following: boolean;
  setFollowing: React.Dispatch<React.SetStateAction<boolean>>;
  setImageOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
}

function ModalContent({ trade, isBuy, isOwner, marketColor, following, setFollowing, setImageOpen, onClose }: ModalContentProps) {
  const { t } = useLang();
  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b shrink-0" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="ticker-chip text-base font-bold">{trade.ticker}</span>
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-bold ${isBuy ? 'badge-long' : 'badge-short'}`}>
            {isBuy ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {trade.direction}
          </span>
          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
            trade.status === 'OPEN' ? 'badge-open'
              : trade.status === 'WIN' ? 'badge-win'
              : trade.status === 'LOSS' ? 'badge-loss' : 'badge-be'
          }`}>
            {trade.status === 'OPEN' ? `● ${t('OPEN')}` : trade.status === 'WIN' ? '✓ WIN' : trade.status === 'LOSS' ? '✗ LOSS' : '~ BE'}
          </span>
          <IdeaTypeBadge ideaType={trade.ideaType} size="md" />
        </div>
        <button onClick={onClose} className="p-2 rounded-lg transition-colors duration-150"
          style={{ color: 'var(--text-muted)', background: 'var(--bg-sunken)' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)'; }}>
          <X size={18} />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
        <div className="p-5 flex flex-col gap-6">
          {/* Author row */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 overflow-hidden"
                style={{ background: trade.author.avatarUrl ? undefined : 'var(--accent)', color: 'var(--bg-base)' }}>
                {trade.author.avatarUrl ? (
                  <img src={trade.author.avatarUrl} alt={trade.author.displayName} className="w-full h-full object-cover" />
                ) : trade.author.initial}
              </div>
              <div>
                <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{trade.author.displayName}</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>@{trade.author.username}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ background: marketColor.bg, color: marketColor.text }}>{trade.market}</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs" style={{ background: 'var(--bg-sunken)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>
                <Clock size={10} />{timeframeLabels[trade.timeframe] ?? trade.timeframe}
              </span>
              {!isOwner && (
                <button
                  onClick={() => {
                    setFollowing((p) => !p);
                    toast.success(following ? `${t('Unfollowed')} @${trade.author.username}` : `${t('Now following')} @${trade.author.username}`);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150"
                  style={{ background: following ? 'var(--bg-sunken)' : 'var(--accent-muted)', color: following ? 'var(--text-secondary)' : 'var(--accent)', border: following ? '1px solid var(--border)' : '1px solid var(--accent)' }}>
                  {following ? <Check size={12} /> : <UserPlus size={12} />}
                  {following ? t('Following') : t('Follow')}
                </button>
              )}
            </div>
          </div>

          {/* Caption */}
          {trade.caption && <p className="text-sm leading-relaxed" style={{ color: 'var(--text-body)' }}>{trade.caption}</p>}

          {/* Signal chart — the static server-drawn chart with a Play → live replay to the outcome
              (TradingView-idea style). Analyses just show their uploaded image. */}
          <SignalChartPlayer trade={trade} height={360} />

          {/* R:R Breakdown + Signal levels — signals only. Analyses carry no entry/SL/TP, so these
              would render empty/nonsensical numeric blocks. */}
          {trade.ideaType !== 'ANALYSIS' && (
            <>
              <RRBreakdown trade={trade} />
              <SignalSection trade={trade} />
            </>
          )}

          {/* External link */}
          {trade.externalUrl && (
            <a href={trade.externalUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm transition-colors duration-100" style={{ color: 'var(--accent)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--accent)')}>
              <ExternalLink size={14} />{t('View original analysis')}
            </a>
          )}

          {/* Created at */}
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {t('Posted')} {parseServerDate(trade.createdAt).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>

          <div style={{ borderTop: '1px solid var(--border)' }} />

          {/* Comments */}
          <CommentsSection tradeId={trade.id} initialComments={trade.comments} />
          <div style={{ height: 16 }} />
        </div>
      </div>

    </>
  );
}

// ─── Main Modal ──────────────────────────────────────────────────────────────

interface TradeDetailModalProps {
  trade: TradeCardData | null;
  isOpen: boolean;
  onClose: () => void;
  currentUserId?: string;
}

export default function TradeDetailModal({ trade, isOpen, onClose, currentUserId }: TradeDetailModalProps) {
  const [imageOpen, setImageOpen] = useState(false);
  const [following, setFollowing] = useState(trade?.author.isFollowing ?? false);

  useEffect(() => { setFollowing(trade?.author.isFollowing ?? false); }, [trade?.author.isFollowing]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!trade) return null;

  const isBuy = trade.direction === 'BUY';
  const isOwner = !!(currentUserId && trade.author.userId && currentUserId === trade.author.userId);
  const marketColor = marketColors[trade.market] ?? { bg: 'var(--accent-muted)', text: 'var(--accent)', border: 'transparent' };

  const contentProps: ModalContentProps = {
    trade, isBuy, isOwner, marketColor, following, setFollowing,
    setImageOpen, onClose,
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[200]"
        style={{
          background: 'rgba(28,43,38,0.6)', backdropFilter: 'blur(4px)',
          opacity: isOpen ? 1 : 0, transition: 'opacity 0.25s ease',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
        onClick={onClose}
      />

      {/* Mobile: bottom sheet */}
      <div
        className="fixed z-[201] overflow-hidden flex flex-col md:hidden"
        style={{
          bottom: 0, left: 0, right: 0,
          borderRadius: '20px 20px 0 0',
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          boxShadow: '0 -24px 80px rgba(28,43,38,0.25)',
          transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
          maxHeight: '92vh',
        }}
      >
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full" style={{ background: 'var(--border)' }} />
        </div>
        <ModalContent {...contentProps} />
      </div>

      {/* Desktop: centered overlay */}
      <div
        className="fixed z-[201] overflow-hidden flex-col hidden md:flex"
        style={{
          top: '50%', left: '50%',
          transform: isOpen ? 'translate(-50%, -50%)' : 'translate(-50%, -48%)',
          opacity: isOpen ? 1 : 0,
          transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.25s ease',
          pointerEvents: isOpen ? 'auto' : 'none',
          width: '100%', maxWidth: 720, maxHeight: '90vh',
          borderRadius: 20, background: 'var(--bg-card)',
          border: '1px solid var(--border)', boxShadow: '0 24px 64px rgba(28,43,38,0.25)',
        }}
      >
        <ModalContent {...contentProps} />
      </div>

      {/* Image lightbox */}
      {imageOpen && trade.imageUrl && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.9)' }} onClick={() => setImageOpen(false)}>
          <img src={trade.imageUrl} alt={`${trade.ticker} chart analysis`}
            className="max-w-full max-h-full rounded-xl object-contain"
            style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.8)', display: 'block' }}
            loading="lazy" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
}
